import { useQueryClient } from '@tanstack/react-query';
import { FC, MouseEvent, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';

import { ActionsDropdownButton } from '@/components/ActionsDropdown';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { api } from '@/utils/api';
import { invoiceStatusLabels } from '@/utils/data/payments';
import { InvoiceStatus, PaymentBank } from '@/utils/enums';

import InvoicesPaidBankModal from './InvoicesPaidBankModal';

const Holder = styled.div`
  position: relative;
`;

const Dot = styled.div<{
  $status: InvoiceStatus;
}>`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  margin-right: 8px;
  ${({ theme, $status }) =>
    $status === InvoiceStatus.Paid &&
    css`
      background: ${theme.colors.success};
    `}
  ${({ theme, $status }) =>
    $status === InvoiceStatus.Received &&
    css`
      background: ${theme.colors.error};
    `}
  ${({ theme, $status }) =>
    $status === InvoiceStatus.PreReceived &&
    css`
      background: ${theme.colors.warning};
    `}
`;

const Status = styled.button<{
  $isDropdownOpen: boolean;
}>`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: 4px;
  display: flex;
  align-items: center;
  padding: 9px 12px;
  width: 130px;
  position: relative;
  svg {
    position: absolute;
    top: calc(50% - 8px);
    right: 12px;
    transition: all ease-out 0.2s;
  }
  ${({ $isDropdownOpen }) =>
    $isDropdownOpen &&
    css`
      svg {
        transform: rotate(180deg);
      }
    `}
  &:disabled {
    cursor: not-allowed;
    color: ${({ theme }) => theme.colors.grayscaleDark};
  }
`;

const GlobalStatusButton = styled(Status)`
  padding: 7px 12px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.gray};
  border-color: ${({ theme }) => theme.colors.grayscale};
  width: 130px;
  font-family: 'Manrope', sans-serif;
  font-weight: 700;
  font-size: 12px;
  line-height: 15px;
  transition: all ease-out 0.2s;
  margin-left: 8px;
  svg {
    right: 10px;
    top: calc(50% - 7px);
  }
  &:hover {
    color: ${({ theme }) => theme.colors.black};
  }
  &:disabled {
    color: ${({ theme }) => theme.colors.grayscaleDark};
    cursor: not-allowed;
  }
`;

const StatusDropdown = styled.div<{ $right: number; $top: number }>`
  position: absolute;
  width: 130px;
  right: calc(100% - ${({ $right }) => $right}px);
  top: ${({ $top }) => $top + 43}px;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  padding: 4px;
  z-index: 1;
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
`;

interface Props {
  invoiceId?: number;
  invoicesIds?: number[];
  status: InvoiceStatus;
  global?: boolean;
  disabled?: boolean;
  setSelectedInvoices?: React.Dispatch<React.SetStateAction<number[]>>;
}

const modalRoot = document.getElementById('modals-root');

const InvoicesStatusDropdown: FC<Props> = ({
  status,
  global = false,
  disabled = false,
  invoiceId,
  invoicesIds,
  setSelectedInvoices,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);

  const ref = useRef(document.createElement('div'));

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const buttonRef = useRef<HTMLButtonElement>(null);

  const componentRef = useRef<HTMLDivElement>(null);

  const domRect = buttonRef.current?.getBoundingClientRect();

  const onChange = async (status: InvoiceStatus, paidByBank?: PaymentBank) => {
    try {
      if (invoiceId) {
        await api.put<
          {
            invoices: number[];
            status: InvoiceStatus;
            paid_by_bank?: PaymentBank;
          },
          { invoices: number[]; status: InvoiceStatus }
        >(`/v1/payments/invoices/status`, {
          invoices: [invoiceId],
          status,
          ...(paidByBank && { paid_by_bank: paidByBank }),
        });
      } else {
        if (!invoicesIds?.length) {
          return alerts.error('Please select at leat one invoice');
        }

        await api.put<
          {
            invoices: number[];
            status: InvoiceStatus;
            paid_by_bank?: PaymentBank;
          },
          { invoices: number[]; status: InvoiceStatus }
        >(`/v1/payments/invoices/status`, {
          invoices: invoicesIds,
          status,
          ...(paidByBank && { paid_by_bank: paidByBank }),
        });

        setSelectedInvoices!([]);
      }

      queryClient.invalidateQueries(['invoices_list'], {
        exact: false,
      });

      alerts.success(
        `${
          invoicesIds ? 'Statuses have' : 'status has'
        }  been successfully updated`,
      );

      setShowDropdown(false);
    } catch (err) {
      alerts.error();
    }
  };

  useEffect(() => {
    const callback = () => setShowDropdown(false);
    window.addEventListener('scroll', callback, true);
    return () => {
      window.removeEventListener('scroll', callback, true);
    };
  }, []);

  useEffect(() => {
    const el = ref.current;
    modalRoot!.appendChild(el);
    return () => {
      modalRoot!.removeChild(el);
    };
  }, []);

  useOnClickOutside(componentRef, () => setShowDropdown(false));

  const renderStatusName = () => invoiceStatusLabels[status];

  return (
    <Holder ref={componentRef}>
      {!!global ? (
        <GlobalStatusButton
          onClick={() => {
            setShowDropdown((prev) => !prev);
          }}
          disabled={!invoicesIds?.length}
          ref={buttonRef}
          $isDropdownOpen={showDropdown}
        >
          Change status
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.33203 5.25L6.9987 9.91667L11.6654 5.25"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </GlobalStatusButton>
      ) : (
        <Status
          onClick={() => {
            setShowDropdown((prev) => !prev);
          }}
          disabled={disabled}
          ref={buttonRef}
          $isDropdownOpen={showDropdown}
        >
          <Dot $status={status} />
          <Text $type="smallBold">{renderStatusName()}</Text>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.33203 5.25L6.9987 9.91667L11.6654 5.25"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Status>
      )}

      {createPortal(
        <>
          {domRect && showDropdown && (
            <StatusDropdown
              $right={domRect.x + domRect.width}
              $top={domRect.y + window.scrollY}
            >
              <ActionsDropdownButton
                as="button"
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  onChange(InvoiceStatus.PreReceived);
                }}
              >
                <Dot $status={InvoiceStatus.PreReceived} />
                {invoiceStatusLabels[InvoiceStatus.PreReceived]}
              </ActionsDropdownButton>
              <ActionsDropdownButton
                as="button"
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  onChange(InvoiceStatus.Received);
                }}
              >
                <Dot $status={InvoiceStatus.Received} />
                Received
              </ActionsDropdownButton>
              <ActionsDropdownButton
                as="button"
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  setShowDropdown(false);
                  setShowBankModal(true);
                }}
              >
                <Dot $status={InvoiceStatus.Paid} />
                Paid
              </ActionsDropdownButton>
            </StatusDropdown>
          )}
        </>,
        ref.current,
      )}

      {showBankModal && (
        <InvoicesPaidBankModal
          onClose={() => setShowBankModal(false)}
          onSelect={async (bank) => {
            await onChange(InvoiceStatus.Paid, bank);
            setShowBankModal(false);
          }}
        />
      )}
    </Holder>
  );
};

export default InvoicesStatusDropdown;
